import {Component, ErrorInfo, PropsWithChildren} from 'react';

type ErrorBoundaryProps = PropsWithChildren<{
    onReload?: () => void
}>

type ErrorBoundaryState = {
    error: Error | undefined
}

/**
 * Catches an error thrown while rendering anything beneath it and shows a message with a way to reload, in place of
 * the blank page React leaves when a render error goes uncaught.
 *
 * It only sees errors thrown during rendering. Errors in event handlers, in promises, or before React has mounted
 * do not reach it.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = {error: undefined}

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {error}
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("Uncaught error while rendering:", error, info.componentStack)
    }

    render() {
        if(!this.state.error) return this.props.children

        const reload = this.props.onReload ?? (() => window.location.reload())

        return (
            <div role="alert"
                 className="min-h-screen flex flex-col items-center justify-center px-4 text-center bg-background text-text dark:bg-background-dark dark:text-text-dark">
                <h1 className="text-xl font-semibold text-heading dark:text-heading-dark">Something went wrong</h1>
                <p className="mt-2 max-w-md text-sm">
                    This page could not be displayed. Reloading usually fixes it. Answers you have already saved are
                    not affected.
                </p>
                <button type="button"
                        className="mt-6 px-4 py-2 rounded-md bg-accent dark:bg-accent-dark text-background dark:text-background-dark opacity-80 hover:opacity-100"
                        onClick={reload}>
                    Reload page
                </button>
            </div>
        )
    }
}
